import React, { useState, useEffect, useCallback } from 'react';
import { useSnapshot } from '../context/SnapshotContext';
import { useOfflineStatus } from '../hooks/useOfflineStatus';
import { getPendingMutations, removeMutation } from '../utils/offlineDB';
import { describeMutation, formatMutationAge } from '../utils/syncEngine';

// ── Badge méthode HTTP ────────────────────────────────────────
const METHOD_COLORS = {
  POST  : { bg: 'rgba(52,211,153,0.15)', color: '#34d399', label: 'POST'   },
  PUT   : { bg: 'rgba(74,158,255,0.15)', color: '#4a9eff', label: 'PUT'    },
  PATCH : { bg: 'rgba(74,158,255,0.15)', color: '#60a5fa', label: 'PATCH'  },
  DELETE: { bg: 'rgba(248,113,113,0.15)',color: '#f87171', label: 'DELETE' },
};

function MethodBadge({ method }) {
  const c = METHOD_COLORS[method] || { bg: '#2a2f42', color: '#b8bdd0', label: method };
  return (
    <span style={{
      display: 'inline-block', padding: '1px 6px',
      borderRadius: 4, fontSize: '0.62rem', fontWeight: 800,
      background: c.bg, color: c.color, fontFamily: 'var(--font-mono)',
      letterSpacing: '0.04em', flexShrink: 0,
    }}>
      {c.label}
    </span>
  );
}

// ── Icône statut ──────────────────────────────────────────────
function StatusIcon({ status }) {
  if (status === 'pending')  return <span style={{ color: '#94a3b8', fontSize: '0.9rem' }}>⏳</span>;
  if (status === 'error')    return <span style={{ color: '#f87171', fontSize: '0.9rem' }}>✕</span>;
  if (status === 'conflict') return <span style={{ color: '#fbbf24', fontSize: '0.9rem' }}>⚠</span>;
  return null;
}

// ── Résumé de sync ────────────────────────────────────────────
function SyncResultBanner({ results }) {
  if (!results) return null;
  const { synced, failed, conflicts, total } = results;
  if (total === 0) return (
    <div className="sync-result-banner sync-result-banner--ok">
      ✓ Aucune modification en attente
    </div>
  );
  return (
    <div className={`sync-result-banner sync-result-banner--${failed + conflicts > 0 ? 'warn' : 'ok'}`}>
      {synced > 0 && <span>✓ {synced} synchronisée{synced > 1 ? 's' : ''}</span>}
      {conflicts > 0 && <span>⚠ {conflicts} conflit{conflicts > 1 ? 's' : ''}</span>}
      {failed > 0 && <span>✕ {failed} erreur{failed > 1 ? 's' : ''}</span>}
    </div>
  );
}

// ── Panneau principal ─────────────────────────────────────────
export function SyncPanel() {
  const {
    showSyncPanel, setShowSyncPanel,
    isSyncing, syncResults, syncProgress,
    syncAndRefresh,
  } = useSnapshot();
  const isOffline = useOfflineStatus();

  const [mutations, setMutations] = useState([]);
  const [discarding, setDiscarding] = useState(null); // id en cours de suppression

  // Charge et recharge la liste à chaque changement de queue
  const loadMutations = useCallback(() => {
    getPendingMutations().then(setMutations).catch(() => setMutations([]));
  }, []);

  useEffect(() => {
    if (!showSyncPanel) return;
    loadMutations();
    window.addEventListener('gf:mutation-queued', loadMutations);
    window.addEventListener('gf:mutation-synced', loadMutations);
    return () => {
      window.removeEventListener('gf:mutation-queued', loadMutations);
      window.removeEventListener('gf:mutation-synced', loadMutations);
    };
  }, [showSyncPanel, loadMutations]);

  if (!showSyncPanel) return null;

  // ── Rejeter une mutation en erreur/conflit ────────────────────
  async function handleDiscard(id) {
    setDiscarding(id);
    try {
      await removeMutation(id);
      loadMutations();
    } finally {
      setDiscarding(null);
    }
  }

  // ── Progress label ────────────────────────────────────────────
  const syncPct = isSyncing && syncProgress.total > 0
    ? Math.round((syncProgress.current / syncProgress.total) * 100)
    : 0;
  const syncLabel = isSyncing
    ? syncProgress.mutation
      ? describeMutation(syncProgress.mutation)
      : 'Préparation…'
    : null;

  const canSync  = !isOffline && !isSyncing && mutations.length > 0;
  const pending  = mutations.filter(m => m.status === 'pending').length;
  const errors   = mutations.filter(m => m.status === 'error' || m.status === 'conflict').length;

  return (
    <>
      {/* Fond semi-transparent */}
      <div className="sync-overlay" onClick={() => !isSyncing && setShowSyncPanel(false)} />

      {/* Panneau */}
      <div className="sync-panel" role="dialog" aria-modal="true" aria-label="Synchronisation">

        {/* En-tête */}
        <div className="sync-panel__header">
          <span className="sync-panel__title">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
              style={{ flexShrink: 0 }}>
              <polyline points="17 1 21 5 17 9"/>
              <path d="M3 11V9a4 4 0 0 1 4-4h14"/>
              <polyline points="7 23 3 19 7 15"/>
              <path d="M21 13v2a4 4 0 0 1-4 4H3"/>
            </svg>
            Synchronisation
          </span>
          <button
            className="sync-panel__close"
            onClick={() => setShowSyncPanel(false)}
            disabled={isSyncing}
            title="Fermer"
          >✕</button>
        </div>

        {/* Corps */}
        <div className="sync-panel__body">

          {/* Résultat de la dernière sync */}
          <SyncResultBanner results={syncResults} />

          {/* État réseau */}
          {isOffline && (
            <div className="sync-info sync-info--warn">
              ⚠ Serveur inaccessible — impossible de synchroniser
            </div>
          )}

          {/* Barre de progression */}
          {isSyncing && (
            <div className="sync-progress-area">
              <div className="sync-progress-bar">
                <div className="sync-progress-bar__fill" style={{ width: `${syncPct}%` }} />
              </div>
              <div className="sync-progress-label">
                {syncLabel} ({syncPct}%)
              </div>
            </div>
          )}

          {/* Liste des mutations */}
          {mutations.length === 0 ? (
            <div className="sync-empty">
              {syncResults
                ? '✓ Toutes les modifications ont été synchronisées'
                : 'Aucune modification en attente'}
            </div>
          ) : (
            <>
              <div className="sync-section-title">
                {pending > 0 && `${pending} en attente`}
                {pending > 0 && errors > 0 && ' · '}
                {errors > 0 && `${errors} en erreur`}
              </div>
              <div className="sync-mutation-list">
                {mutations.map(m => (
                  <div
                    key={m.id}
                    className={`sync-mutation-item sync-mutation-item--${m.status}`}
                  >
                    <div className="sync-mutation-item__main">
                      <StatusIcon status={m.status} />
                      <MethodBadge method={m.method} />
                      <span className="sync-mutation-item__desc">
                        {describeMutation(m)}
                      </span>
                      <span className="sync-mutation-item__age">
                        {formatMutationAge(m.timestamp)}
                      </span>
                    </div>

                    {/* Message d'erreur */}
                    {m.error_msg && (
                      <div className="sync-mutation-item__error">
                        {m.error_msg}
                      </div>
                    )}

                    {/* Action : ignorer mutation en erreur ou conflit */}
                    {(m.status === 'error' || m.status === 'conflict') && (
                      <div className="sync-mutation-item__actions">
                        <button
                          className="sync-btn-discard"
                          onClick={() => handleDiscard(m.id)}
                          disabled={discarding === m.id}
                          title="Ignorer cette modification (elle sera perdue)"
                        >
                          {discarding === m.id ? '…' : 'Ignorer'}
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Pied */}
        <div className="sync-panel__footer">
          <span className="sync-panel__hint">
            {isOffline
              ? 'Reconnectez-vous pour synchroniser'
              : mutations.length === 0
                ? 'Données à jour'
                : 'Les modifications seront envoyées dans l\'ordre chronologique'}
          </span>
          <button
            className={`sync-btn-primary${!canSync ? ' sync-btn-primary--disabled' : ''}`}
            onClick={() => canSync && syncAndRefresh()}
            disabled={!canSync}
            title={isOffline ? 'Connexion requise' : isSyncing ? 'Synchronisation en cours…' : 'Synchroniser maintenant'}
          >
            {isSyncing ? (
              <>
                <svg className="snapshot-spin" width="13" height="13" viewBox="0 0 24 24"
                  fill="none" stroke="currentColor" strokeWidth="2.5"
                  strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
                </svg>
                {syncPct}%
              </>
            ) : (
              <>
                ↕ Synchroniser{mutations.length > 0 ? ` (${mutations.length})` : ''}
              </>
            )}
          </button>
        </div>
      </div>
    </>
  );
}
